import { useState, useEffect } from 'react';
import api from '@/lib/api';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Switch } from '@/components/ui/switch';
import { Share2, Users, DollarSign, TrendingUp, Clock, CheckCircle, XCircle, Plus, Edit, Copy, Loader2, RefreshCw, Settings, Search, UserPlus } from 'lucide-react';
import { toast } from 'sonner';

export default function AdminAffiliates() {
  const [data, setData] = useState({ affiliates: [], total: 0, stats: {} });
  const [withdrawals, setWithdrawals] = useState([]);
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(null);
  const [showEditDialog, setShowEditDialog] = useState(false);
  const [editingAffiliate, setEditingAffiliate] = useState(null);
  const [editForm, setEditForm] = useState({ status: '', commission_rate: 20 });

  // Manual add affiliate dialog state
  const [showAddDialog, setShowAddDialog] = useState(false);
  const [eligibleTenants, setEligibleTenants] = useState([]);
  const [eligibleLoading, setEligibleLoading] = useState(false);
  const [tenantSearch, setTenantSearch] = useState('');
  const [selectedTenantId, setSelectedTenantId] = useState('');
  const [addCommissionRate, setAddCommissionRate] = useState(20);
  const [adding, setAdding] = useState(false);
  
  // Recurring commission settings
  const [recurringSettings, setRecurringSettings] = useState({
    enabled: true,
    percentage: 10,
    duration_months: 12,
    min_subscription_months: 1
  });

  // General settings — default commission rate for new affiliates
  const [generalSettings, setGeneralSettings] = useState({ default_commission_rate: 20 });
  const [savingGeneral, setSavingGeneral] = useState(false);

  const fetch = async () => {
    setLoading(true);
    try {
      const [affRes, wdRes, recurringRes, generalRes] = await Promise.all([
        api.get('/admin/affiliates'),
        api.get('/admin/affiliates/withdrawals'),
        api.get('/admin/affiliates/recurring-settings'),
        api.get('/admin/affiliates/general-settings'),
      ]);
      setData(affRes.data);
      setWithdrawals(wdRes.data.withdrawals || []);
      setRecurringSettings(recurringRes.data.settings || {});
      setGeneralSettings(generalRes.data.settings || { default_commission_rate: 20 });
    } catch {} finally { setLoading(false); }
  };

  const saveGeneralSettings = async () => {
    setSavingGeneral(true);
    try {
      const rate = Math.max(0, Math.min(100, parseInt(generalSettings.default_commission_rate) || 0));
      await api.put('/admin/affiliates/general-settings', { default_commission_rate: rate });
      toast.success(`Komisi default disimpan: ${rate}% (hanya berlaku untuk affiliate baru)`);
      fetch();
    } catch {
      toast.error('Gagal menyimpan komisi default');
    } finally {
      setSavingGeneral(false);
    }
  };

  useEffect(() => { fetch(); }, []);

  const openAddDialog = async () => {
    setSelectedTenantId('');
    setTenantSearch('');
    setAddCommissionRate(generalSettings.default_commission_rate || 20);
    setShowAddDialog(true);
    setEligibleLoading(true);
    try {
      const res = await api.get('/admin/affiliates/eligible-tenants');
      setEligibleTenants(res.data.tenants || []);
    } catch {
      toast.error('Gagal memuat daftar tenant');
    } finally {
      setEligibleLoading(false);
    }
  };

  const handleCreateAffiliate = async () => {
    if (!selectedTenantId) {
      toast.error('Pilih tenant dulu');
      return;
    }
    setAdding(true);
    try {
      const res = await api.post('/admin/affiliates', {
        tenant_id: selectedTenantId,
        commission_rate: addCommissionRate,
      });
      toast.success(res.data.message || `Affiliate dibuat! Kode: ${res.data.code}`);
      setShowAddDialog(false);
      fetch();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Gagal membuat affiliate');
    } finally {
      setAdding(false);
    }
  };

  const handleApprove = async (aff) => {
    setProcessing(aff.id);
    try {
      await api.put(`/admin/affiliates/${aff.id}`, { status: 'active', commission_rate: aff.commission_rate });
      toast.success(`Pendaftaran ${aff.user?.name || aff.code} disetujui`);
      fetch();
    } catch {
      toast.error('Gagal approve affiliate');
    } finally {
      setProcessing(null);
    }
  };

  const handleReject = async (aff) => {
    if (!window.confirm(`Tolak pendaftaran affiliate "${aff.user?.name || aff.code}"?`)) return;
    setProcessing(aff.id);
    try {
      await api.put(`/admin/affiliates/${aff.id}`, { status: 'rejected', commission_rate: aff.commission_rate });
      toast.success('Pendaftaran ditolak');
      fetch();
    } catch {
      toast.error('Gagal reject');
    } finally {
      setProcessing(null);
    }
  };

  const filteredEligibleTenants = eligibleTenants.filter((t) => {
    const q = tenantSearch.trim().toLowerCase();
    if (!q) return true;
    return [t.tenant_name, t.subdomain, t.owner_name, t.owner_email]
      .filter(Boolean).some((s) => s.toLowerCase().includes(q));
  });

  const openEditDialog = (aff) => {
    setEditingAffiliate(aff);
    setEditForm({ status: aff.status, commission_rate: aff.commission_rate });
    setShowEditDialog(true);
  };

  const handleUpdateAffiliate = async () => {
    try {
      await api.put(`/admin/affiliates/${editingAffiliate.id}`, editForm);
      toast.success('Affiliate diupdate');
      setShowEditDialog(false);
      fetch();
    } catch (e) {
      toast.error('Gagal update affiliate');
    }
  };

  const handleWithdrawal = async (id, status) => {
    setProcessing(id);
    try {
      await api.put(`/admin/affiliates/withdrawals/${id}`, { status, note: '' });
      toast.success(`Withdrawal ${status}`);
      fetch();
    } catch (e) {
      toast.error('Gagal proses withdrawal');
    } finally {
      setProcessing(null);
    }
  };

  const copyCode = (code) => {
    navigator.clipboard.writeText(code);
    toast.success('Kode disalin');
  };

  const fmt = (v) => new Intl.NumberFormat('id-ID').format(v || 0);
  const statusColor = (s) => {
    if (s === 'active' || s === 'approved') return 'bg-emerald-100 text-emerald-700';
    if (s === 'pending') return 'bg-amber-100 text-amber-700';
    if (s === 'inactive' || s === 'rejected') return 'bg-red-100 text-red-700';
    return 'bg-slate-100 text-slate-700';
  };

  const stats = data.stats || {};

  return (
    <div data-testid="admin-affiliates-page">
      <div className="flex items-center justify-between mb-6">
        <div>
          <h1 className="text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Plus Jakarta Sans' }}>SaaS Affiliates</h1>
          <p className="text-sm text-slate-500">Kelola program affiliate platform</p>
        </div>
        <Button onClick={openAddDialog} className="bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="create-affiliate-btn">
          <UserPlus size={16} className="mr-2" /> Tambah Affiliate Manual
        </Button>
      </div>

      {/* Stats */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-blue-50 flex items-center justify-center">
              <Users size={20} className="text-[#2D6CDF]" />
            </div>
            <div>
              <p className="text-xl font-bold">{stats.total_affiliates || 0}</p>
              <p className="text-xs text-slate-500">Total Affiliates</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-emerald-50 flex items-center justify-center">
              <DollarSign size={20} className="text-emerald-600" />
            </div>
            <div>
              <p className="text-xl font-bold">Rp {fmt(stats.total_commission)}</p>
              <p className="text-xs text-slate-500">Total Komisi</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-amber-50 flex items-center justify-center">
              <Clock size={20} className="text-amber-600" />
            </div>
            <div>
              <p className="text-xl font-bold">{stats.pending_withdrawals || 0}</p>
              <p className="text-xs text-slate-500">Pending Withdrawal</p>
            </div>
          </CardContent>
        </Card>
        <Card className="border-slate-200 shadow-sm">
          <CardContent className="p-4 flex items-center gap-3">
            <div className="w-10 h-10 rounded-lg bg-violet-50 flex items-center justify-center">
              <TrendingUp size={20} className="text-violet-600" />
            </div>
            <div>
              <p className="text-xl font-bold">{stats.default_commission_rate ?? generalSettings.default_commission_rate ?? 20}%</p>
              <p className="text-xs text-slate-500">Default Rate</p>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Default Commission Rate Setting */}
      <Card className="border-slate-200 shadow-sm mb-6">
        <CardContent className="p-5">
          <div className="flex items-start gap-4 flex-col sm:flex-row sm:items-center sm:justify-between">
            <div className="flex-1">
              <Label className="text-base font-semibold flex items-center gap-2">
                <TrendingUp size={16} className="text-violet-600" /> Komisi Default Affiliate Baru
              </Label>
              <p className="text-sm text-slate-500 mt-1">
                Berlaku untuk affiliate yang <strong>didaftarkan setelah</strong> setting ini disimpan.
                Affiliate yang sudah ada <strong>tidak ikut berubah</strong> — bisa diatur satu per satu via tombol Edit di tabel.
              </p>
            </div>
            <div className="flex items-end gap-2 shrink-0">
              <div>
                <Label className="text-xs text-slate-500">Komisi Default (%)</Label>
                <Input
                  type="number"
                  min={0}
                  max={100}
                  value={generalSettings.default_commission_rate}
                  onChange={(e) => setGeneralSettings({ default_commission_rate: parseInt(e.target.value) || 0 })}
                  className="w-28 mt-1"
                  data-testid="default-commission-rate-input"
                />
              </div>
              <Button
                onClick={saveGeneralSettings}
                disabled={savingGeneral}
                className="bg-[#2D6CDF] hover:bg-[#1A5BC4]"
                data-testid="save-default-rate-btn"
              >
                {savingGeneral ? <Loader2 size={14} className="animate-spin" /> : 'Simpan'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>

      <Tabs defaultValue="affiliates">
        <TabsList>
          <TabsTrigger value="affiliates">Affiliates ({data.total})</TabsTrigger>
          <TabsTrigger value="pending">
            Pending Approval ({data.affiliates.filter(a => a.status === 'pending').length})
          </TabsTrigger>
          <TabsTrigger value="withdrawals">Withdrawals ({withdrawals.filter(w => w.status === 'pending').length} pending)</TabsTrigger>
          <TabsTrigger value="recurring"><Settings className="h-4 w-4 mr-1" />Recurring</TabsTrigger>
        </TabsList>

        <TabsContent value="affiliates" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {loading ? (
                <div className="py-12 flex justify-center"><div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#2D6CDF]" /></div>
              ) : (
                <div className="overflow-x-auto">
                  <table className="w-full text-sm">
                    <thead><tr className="border-b border-slate-100 bg-slate-50">
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">User</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Kode</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Clicks</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Signups</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Subs</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Komisi</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Rate</th>
                      <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                      <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                    </tr></thead>
                    <tbody>
                      {data.affiliates.map((a) => (
                        <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                          <td className="py-3 px-4">
                            <p className="font-medium text-slate-900">{a.user?.name || '-'}</p>
                            <p className="text-xs text-slate-500">{a.user?.email}</p>
                          </td>
                          <td className="py-3 px-4">
                            <div className="flex items-center gap-2">
                              <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono">{a.code}</code>
                              <Button variant="ghost" size="icon" className="h-6 w-6" onClick={() => copyCode(a.code)}>
                                <Copy size={12} />
                              </Button>
                            </div>
                          </td>
                          <td className="py-3 px-4 text-slate-600">{a.total_clicks}</td>
                          <td className="py-3 px-4 text-slate-600">{a.total_signups}</td>
                          <td className="py-3 px-4 text-slate-600">{a.total_subscriptions}</td>
                          <td className="py-3 px-4 font-medium">Rp {fmt(a.total_commission)}</td>
                          <td className="py-3 px-4 text-slate-600">{a.commission_rate}%</td>
                          <td className="py-3 px-4"><Badge className={statusColor(a.status)}>{a.status}</Badge></td>
                          <td className="py-3 px-4 text-right">
                            <Button variant="ghost" size="icon" onClick={() => openEditDialog(a)} data-testid={`edit-affiliate-${a.id}`}>
                              <Edit size={14} />
                            </Button>
                          </td>
                        </tr>
                      ))}
                      {!data.affiliates.length && <tr><td colSpan={9} className="py-12 text-center text-slate-400">Belum ada affiliate</td></tr>}
                    </tbody>
                  </table>
                </div>
              )}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="pending" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              {(() => {
                const pendingList = (data.affiliates || []).filter(a => a.status === 'pending');
                if (pendingList.length === 0) {
                  return <p className="py-12 text-center text-slate-400">Tidak ada pendaftaran yang menunggu approval</p>;
                }
                return (
                  <div className="overflow-x-auto">
                    <table className="w-full text-sm">
                      <thead><tr className="border-b border-slate-100 bg-slate-50">
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">User</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Kode</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Rate</th>
                        <th className="text-left py-3 px-4 text-slate-500 font-medium">Daftar Pada</th>
                        <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                      </tr></thead>
                      <tbody>
                        {pendingList.map((a) => (
                          <tr key={a.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                            <td className="py-3 px-4">
                              <p className="font-medium text-slate-900">{a.user?.name || '-'}</p>
                              <p className="text-xs text-slate-500">{a.user?.email}</p>
                            </td>
                            <td className="py-3 px-4">
                              <code className="px-2 py-1 bg-slate-100 rounded text-xs font-mono">{a.code}</code>
                            </td>
                            <td className="py-3 px-4 text-slate-600">{a.commission_rate}%</td>
                            <td className="py-3 px-4 text-slate-500">{new Date(a.created_at).toLocaleDateString('id-ID')}</td>
                            <td className="py-3 px-4 text-right">
                              <div className="flex justify-end gap-1">
                                <Button size="sm" onClick={() => handleApprove(a)} disabled={processing === a.id} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`approve-aff-${a.id}`}>
                                  {processing === a.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                                  Setujui
                                </Button>
                                <Button size="sm" variant="outline" onClick={() => handleReject(a)} disabled={processing === a.id} className="text-red-500 border-red-200 hover:bg-red-50">
                                  <XCircle size={14} className="mr-1" /> Tolak
                                </Button>
                              </div>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                );
              })()}
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="withdrawals" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardContent className="p-0">
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead><tr className="border-b border-slate-100 bg-slate-50">
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Affiliate</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Jumlah</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Bank</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Tanggal</th>
                    <th className="text-left py-3 px-4 text-slate-500 font-medium">Status</th>
                    <th className="text-right py-3 px-4 text-slate-500 font-medium">Aksi</th>
                  </tr></thead>
                  <tbody>
                    {withdrawals.map((w) => (
                      <tr key={w.id} className="border-b border-slate-50 hover:bg-slate-50/50">
                        <td className="py-3 px-4">
                          <p className="font-medium text-slate-900">{w.affiliate?.user?.name || '-'}</p>
                          <p className="text-xs text-slate-500">{w.affiliate?.code}</p>
                        </td>
                        <td className="py-3 px-4 font-medium">Rp {fmt(w.amount)}</td>
                        <td className="py-3 px-4 text-slate-600">{w.bank_name} - {w.bank_account}</td>
                        <td className="py-3 px-4 text-slate-500">{new Date(w.created_at).toLocaleDateString('id-ID')}</td>
                        <td className="py-3 px-4"><Badge className={statusColor(w.status)}>{w.status}</Badge></td>
                        <td className="py-3 px-4 text-right">
                          {w.status === 'pending' && (
                            <div className="flex justify-end gap-1">
                              <Button size="sm" onClick={() => handleWithdrawal(w.id, 'approved')} disabled={processing === w.id} className="bg-emerald-500 hover:bg-emerald-600" data-testid={`approve-wd-${w.id}`}>
                                {processing === w.id ? <Loader2 size={14} className="animate-spin" /> : <CheckCircle size={14} className="mr-1" />}
                                Approve
                              </Button>
                              <Button size="sm" variant="outline" onClick={() => handleWithdrawal(w.id, 'rejected')} disabled={processing === w.id} className="text-red-500 border-red-200 hover:bg-red-50">
                                <XCircle size={14} className="mr-1" /> Tolak
                              </Button>
                            </div>
                          )}
                        </td>
                      </tr>
                    ))}
                    {!withdrawals.length && <tr><td colSpan={6} className="py-12 text-center text-slate-400">Belum ada withdrawal</td></tr>}
                  </tbody>
                </table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        {/* Recurring Commission Tab */}
        <TabsContent value="recurring" className="mt-4">
          <Card className="border-slate-200 shadow-sm">
            <CardHeader>
              <CardTitle>Pengaturan Komisi Recurring</CardTitle>
              <CardDescription>Atur komisi berulang untuk affiliate setiap kali referral mereka memperpanjang langganan</CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="flex items-center justify-between p-4 bg-muted rounded-lg">
                <div>
                  <Label className="text-base">Aktifkan Komisi Recurring</Label>
                  <p className="text-sm text-muted-foreground">Affiliate mendapat komisi setiap bulan selama referral masih berlangganan</p>
                </div>
                <Switch 
                  checked={recurringSettings.enabled}
                  onCheckedChange={(v) => setRecurringSettings({ ...recurringSettings, enabled: v })}
                />
              </div>
              
              <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                <div>
                  <Label>Persentase Komisi Recurring (%)</Label>
                  <Input 
                    type="number"
                    value={recurringSettings.percentage}
                    onChange={(e) => setRecurringSettings({ ...recurringSettings, percentage: parseInt(e.target.value) || 0 })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Lebih rendah dari komisi awal (biasanya 10-15%)</p>
                </div>
                <div>
                  <Label>Durasi Maksimal (Bulan)</Label>
                  <Input 
                    type="number"
                    value={recurringSettings.duration_months}
                    onChange={(e) => setRecurringSettings({ ...recurringSettings, duration_months: parseInt(e.target.value) || 12 })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Berapa bulan affiliate dapat komisi recurring</p>
                </div>
                <div>
                  <Label>Min. Bulan Berlangganan</Label>
                  <Input 
                    type="number"
                    value={recurringSettings.min_subscription_months}
                    onChange={(e) => setRecurringSettings({ ...recurringSettings, min_subscription_months: parseInt(e.target.value) || 1 })}
                  />
                  <p className="text-xs text-muted-foreground mt-1">Referral harus aktif minimal sekian bulan</p>
                </div>
              </div>

              <div className="flex gap-2">
                <Button 
                  onClick={async () => {
                    try {
                      await api.put('/admin/affiliates/recurring-settings', recurringSettings);
                      toast.success('Pengaturan recurring berhasil disimpan');
                    } catch {
                      toast.error('Gagal menyimpan pengaturan');
                    }
                  }}
                  className="bg-[#2D6CDF] hover:bg-[#1A5BC4]"
                >
                  Simpan Pengaturan
                </Button>
                <Button 
                  variant="outline"
                  onClick={async () => {
                    try {
                      const res = await api.post('/admin/affiliates/process-recurring');
                      toast.success(`Proses selesai: ${res.data.processed} komisi diproses, total Rp ${res.data.total_commission.toLocaleString('id-ID')}`);
                    } catch {
                      toast.error('Gagal memproses komisi recurring');
                    }
                  }}
                >
                  <RefreshCw className="h-4 w-4 mr-2" /> Proses Komisi Bulan Ini
                </Button>
              </div>

              <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                <p className="text-sm text-blue-800">
                  <strong>Cara Kerja:</strong> Setiap bulan (via cron job atau manual), sistem akan menghitung komisi recurring untuk semua affiliate yang memiliki referral aktif. 
                  Komisi dihitung berdasarkan persentase dari pembayaran langganan referral.
                </p>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* Manual Add Affiliate Dialog */}
      <Dialog open={showAddDialog} onOpenChange={setShowAddDialog}>
        <DialogContent className="max-w-xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2"><UserPlus size={18} /> Tambah Affiliate Manual</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="bg-blue-50 border border-blue-200 rounded-lg p-3 text-sm text-blue-800">
              Pilih <strong>tenant</strong> yang ingin didaftarkan sebagai affiliate. Affiliate yang dibuat manual oleh admin langsung berstatus <code className="px-1 rounded bg-white border border-blue-200">active</code> tanpa perlu approval.
            </div>

            <div>
              <Label>Cari Tenant</Label>
              <div className="relative mt-1">
                <Search size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-slate-400" />
                <Input
                  placeholder="Cari nama toko, subdomain, atau email pemilik..."
                  value={tenantSearch}
                  onChange={(e) => setTenantSearch(e.target.value)}
                  className="pl-9"
                  data-testid="tenant-search-input"
                />
              </div>
            </div>

            <div className="border border-slate-200 rounded-lg max-h-72 overflow-y-auto">
              {eligibleLoading ? (
                <div className="p-6 flex justify-center text-slate-500"><Loader2 size={18} className="animate-spin mr-2" /> Memuat tenant...</div>
              ) : filteredEligibleTenants.length === 0 ? (
                <p className="p-6 text-center text-sm text-slate-400">
                  {tenantSearch ? 'Tidak ada tenant cocok dengan kata kunci.' : 'Semua tenant sudah jadi affiliate, atau belum ada tenant aktif.'}
                </p>
              ) : (
                <ul className="divide-y divide-slate-100">
                  {filteredEligibleTenants.map((t) => (
                    <li key={t.tenant_id}>
                      <label className={`flex items-center gap-3 p-3 cursor-pointer hover:bg-slate-50 transition-colors ${selectedTenantId === t.tenant_id ? 'bg-[#2D6CDF]/5' : ''}`}>
                        <input
                          type="radio"
                          name="tenant"
                          value={t.tenant_id}
                          checked={selectedTenantId === t.tenant_id}
                          onChange={() => setSelectedTenantId(t.tenant_id)}
                          className="accent-[#2D6CDF]"
                        />
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">{t.tenant_name}</p>
                          <p className="text-xs text-slate-500 truncate">
                            {t.subdomain && <code className="px-1 rounded bg-slate-100 mr-1">{t.subdomain}</code>}
                            {t.owner_name} · {t.owner_email}
                          </p>
                        </div>
                      </label>
                    </li>
                  ))}
                </ul>
              )}
            </div>

            <div>
              <Label>Commission Rate (%)</Label>
              <Input
                type="number"
                min={0}
                max={100}
                value={addCommissionRate}
                onChange={(e) => setAddCommissionRate(parseInt(e.target.value) || 0)}
                className="mt-1"
              />
              <p className="text-[11px] text-slate-500 mt-1">Default 20%. Bisa diubah per affiliate setelah dibuat.</p>
            </div>

            <div className="flex justify-end gap-2 pt-2">
              <Button variant="outline" onClick={() => setShowAddDialog(false)} disabled={adding}>Batal</Button>
              <Button
                onClick={handleCreateAffiliate}
                disabled={adding || !selectedTenantId}
                className="bg-[#2D6CDF] hover:bg-[#1A5BC4]"
                data-testid="confirm-add-affiliate-btn"
              >
                {adding ? <><Loader2 size={14} className="animate-spin mr-2" /> Menambahkan...</> : 'Tambahkan Affiliate'}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>

      {/* Edit Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Edit Affiliate</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label>Status</Label>
              <Select value={editForm.status} onValueChange={(v) => setEditForm({ ...editForm, status: v })}>
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Commission Rate (%)</Label>
              <Input type="number" value={editForm.commission_rate} onChange={(e) => setEditForm({ ...editForm, commission_rate: parseInt(e.target.value) || 0 })} />
            </div>
            <Button onClick={handleUpdateAffiliate} className="w-full bg-[#2D6CDF] hover:bg-[#1A5BC4]" data-testid="save-affiliate-btn">
              Simpan
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
